import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { IoCloseSharp } from "react-icons/io5";
import styled from "styled-components";

import { VStack } from "./containers/stacks";
import { ColorsSystem } from "./colors";

export const MODAL_MIN_WIDTH = "650px";

interface ModalContextProps {
  children: ReactNode;
}

interface ModalContextValue {
  modal: boolean;
  modalContent: ReactNode;
  handleModal: (content?: ReactNode) => void;
}

export const ModalContext = createContext<ModalContextValue>({
  modal: false,
  modalContent: null,
  handleModal: () => {},
});

const { Provider } = ModalContext;

/**
 * Modal Provider
 */
export const ModalProvider = ({ children }: ModalContextProps) => {
  const { modal, handleModal, modalContent } = useModal();

  useEffect(() => {
    if (!modal) {
      return;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [modal]);

  return (
    <Provider
      value={{
        modal,
        handleModal,
        modalContent,
      }}
    >
      <Modal />

      {children}
    </Provider>
  );
};

/**
 * Modal state & actions
 */
export const useModal = () => {
  const [modal, setModal] = useState(false);
  const [modalContent, setModalContent] = useState<ReactNode>(null);

  const handleModal = (content?: ReactNode) => {
    if (content !== undefined) {
      setModalContent(content);
      setModal(true);

      return;
    }

    setModal(false);
    setModalContent(null);
  };

  return {
    modal,
    modalContent,
    handleModal,
  };
};

/**
 * Modal component
 */
export const Modal = () => {
  const { modal, modalContent, handleModal } = useContext(ModalContext);

  if (!modal) {
    return null;
  }

  const modalRoot = document.querySelector("#modal-root");

  if (!modalRoot) {
    return null;
  }

  return ReactDOM.createPortal(
    <Overlay>
      <ModalContainer>
        <CloseButton
          type="button"
          aria-label="Fermer"
          onClick={() => handleModal()}
        >
          <IoCloseSharp color={ColorsSystem.darkGrey} size={28} />
        </CloseButton>

        <ModalContent>{modalContent}</ModalContent>
      </ModalContainer>
    </Overlay>,
    modalRoot,
  );
};

/**
 * Access to modal context
 */
export const useModalContext = () => {
  return useContext(ModalContext);
};

/**
 * Full screen overlay
 */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;

  padding: 20px;

  box-sizing: border-box;

  background: rgba(0, 0, 0, 0.6);
`;

/**
 * Modal container
 *
 * Desktop:
 * - adapts to its content
 * - minimum width: 650px
 * - maximum width: 90vw
 * - maximum height: 90vh
 *
 * Mobile:
 * - width: 90vw
 * - no minimum width
 */
const ModalContainer = styled(VStack)`
  position: relative;

  width: fit-content;
  min-width: ${MODAL_MIN_WIDTH};
  max-width: 90vw;
  max-height: 90vh;

  padding: 32px;

  box-sizing: border-box;

  background: white;
  border-radius: 8px;

  box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);

  overflow: hidden;

  @media (max-width: 690px) {
    width: 90vw;
    min-width: 0;
    max-width: 90vw;

    padding: 24px;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;

    padding: 20px;

    border-radius: 6px;
  }
`;

/**
 * Scrollable modal content
 */
const ModalContent = styled.div`
  width: 100%;
  max-width: 100%;

  max-height: calc(90vh - 64px);

  box-sizing: border-box;

  overflow-x: auto;
  overflow-y: auto;

  overflow-wrap: anywhere;

  @media (max-width: 690px) {
    max-height: calc(90vh - 48px);
  }

  @media (max-width: 480px) {
    max-height: calc(100vh - 40px);
  }
`;

/**
 * Close button
 */
const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;

  padding: 0;

  border: none;
  border-radius: 4px;

  background: white;

  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }

  &:focus-visible {
    outline: 2px solid ${ColorsSystem.darkGrey};
    outline-offset: 2px;
  }
`;
